<div *ngIf="isLoading" style="padding: 16px;">
  <app-ch-skeleton variant="table" [rows]="10" [cols]="8"></app-ch-skeleton>
</div>

<div *ngIf="!isSubmitted && !isLoading">
  <div class="table-header" [class.dark-mode]="isDarkMode()">
    <div class="header-left">
      <button class="back-btn" type="button" (click)="goBack()" title="Back to Heat Plan Form">
        <mat-icon>arrow_back</mat-icon>
        <span>Back</span>
      </button>
    </div>

    <h2 class="tableTitle">
      Heatplan Creation Page<ng-container *ngIf="vesselName || vesselIMO || formattedHeatplanNo"> — </ng-container>
      <span *ngIf="vesselName">{{ vesselName }}</span>
      <span *ngIf="vesselIMO"><ng-container *ngIf="vesselName"> / </ng-container>{{ vesselIMO }}</span>
      <span *ngIf="formattedHeatplanNo"><ng-container *ngIf="vesselName || vesselIMO"> / </ng-container>{{
        formattedHeatplanNo }}</span>
    </h2>
  </div>

  <!-- Glass summary bar (hover particulars only — no bunker tiles) -->
  <div class="cargo-glass-bar" [class.dark-mode]="isDarkMode()" [class.light-mode]="!isDarkMode()">
    <div class="hover-placeholder" *ngIf="!hoverSummary.active">
      <mat-icon>info</mat-icon>
      <span>Tank particulars appear here once the table has tank columns</span>
    </div>
    <ng-container *ngIf="hoverSummary.active">
      <!-- Cargo properties -->
      <div class="pill-group">
        <span class="pill-group__title">Cargo</span>
        <div class="pill-grid">
          <div class="cargo-pill tank-pill" [class.is-preview]="isTankPreview"
            [title]="isTankPreview ? 'Previewing the hovered tank. Click a tank header to pin it.' : 'Pinned tank. Hover a tank column to preview it, click its header to pin it.'">
            <mat-icon>{{ isTankPreview ? 'visibility' : 'push_pin' }}</mat-icon>
            <div class="pill-text">
              <span class="label">{{ isTankPreview ? 'Previewing' : 'Tank' }}: </span>
              <span class="value">{{ activeTankLabel }}</span>
            </div>
          </div>
          <div class="cargo-pill">
            <mat-icon>inventory_2</mat-icon>
            <div class="pill-text">
              <span class="label">Type: </span>
              <span class="value">{{ hoverSummary.cargoType }}</span>
            </div>
          </div>
          <div class="cargo-pill">
            <mat-icon>science</mat-icon>
            <div class="pill-text">
              <span class="label">Sub Type: </span>
              <span class="value">{{ hoverSummary.cargoSubType }}</span>
            </div>
          </div>
          <div class="cargo-pill" *ngIf="hoverSummary.cargoQty !== null">
            <mat-icon>scale</mat-icon>
            <div class="pill-text">
              <span class="label">Qty: </span>
              <span class="value">{{ hoverSummary.cargoQty | number:'1.0-0' }} MT</span>
            </div>
          </div>
          <div class="cargo-pill" *ngIf="hoverSummary.loadTemp !== null">
            <mat-icon>device_thermostat</mat-icon>
            <div class="pill-text">
              <span class="label">Load Temp: </span>
              <span class="value">{{ hoverSummary.loadTemp }} °C</span>
            </div>
          </div>
          <div class="cargo-pill" *ngIf="hoverSummary.pourPoint !== null">
            <mat-icon>ac_unit</mat-icon>
            <div class="pill-text">
              <span class="label">Pour Point: </span>
              <span class="value">{{ hoverSummary.pourPoint }} °C</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Voyage properties (ranges) -->
      <div class="pill-group pill-group--ranges">
        <span class="pill-group__title">Voyage</span>
        <div class="pill-grid">
          <div class="cargo-pill port" [title]="'Load Port: ' + hoverSummary.loadPort">
            <mat-icon>anchor</mat-icon>
            <div class="pill-text">
              <span class="label">Load Port: </span>
              <span class="value">{{ hoverSummary.loadPort }}</span>
            </div>
          </div>
          <div class="cargo-pill range">
            <mat-icon>thermostat</mat-icon>
            <div class="pill-text">
              <span class="label">Voyage Range: </span>
              <span class="value">{{ formatRange(hoverSummary.voyMin, hoverSummary.voyMax) }}</span>
            </div>
          </div>
          <div class="cargo-pill meraki" title="Voyage temperature range recommended by Meraki for this tank">
            <mat-icon>verified</mat-icon>
            <div class="pill-text">
              <span class="label">Meraki Recommended: </span>
              <span class="value">{{ formatRange(hoverSummary.voyMinMeraki, hoverSummary.voyMaxMeraki) }}</span>
            </div>
          </div>
          <div class="cargo-pill range">
            <mat-icon>local_shipping</mat-icon>
            <div class="pill-text">
              <span class="label">Discharge Range: </span>
              <span class="value">{{ formatRange(hoverSummary.dischMin, hoverSummary.dischMax) }}</span>
            </div>
          </div>
          <div class="cargo-pill port" [title]="'Discharge Port: ' + hoverSummary.dischargePort">
            <mat-icon>place</mat-icon>
            <div class="pill-text">
              <span class="label">Discharge Port: </span>
              <span class="value">{{ hoverSummary.dischargePort }}</span>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>

  <div class="forecast-filters" [class.dark-mode]="isDarkMode()" [class.light-mode]="!isDarkMode()">
    <!-- Start Date Info Box -->
    <div class="forecast-filter forecast-filter--start-date" *ngIf="getSecondRowDateFormatted()">
      <label class="forecast-filter__label">Plan Start Date</label>
      <div class="start-date-box" [class.dark-mode]="isDarkMode()">
        <mat-icon class="start-date-icon">event</mat-icon>
        <span><strong>{{ getSecondRowDateFormatted() }}</strong> Onwards</span>
      </div>
    </div>

    <div class="forecast-filter forecast-filter--multi">
      <app-pc-multi-select-filter [lightMode]="!isDarkMode()" label="View Cons based on Cargo"
        placeholder="View Cons based on Cargo" allLabel="All" searchPlaceholder="Search Cons based on Cargo..."
        [options]="cargoSubTypeOptions" [selected]="selectedCargoSubTypes"
        (selectedChange)="onCargoSubTypesChanged($event)">
      </app-pc-multi-select-filter>
    </div>

    <div class="forecast-filter forecast-filter--multi">
      <app-pc-multi-select-filter [lightMode]="!isDarkMode()" label="View Cons based on Tank"
        placeholder="View Cons based on Tank" allLabel="All" searchPlaceholder="Search Cons based on Tank..."
        [options]="tankFilterOptions" [selected]="selectedTanks" (selectedChange)="onTanksChanged($event)">
      </app-pc-multi-select-filter>
    </div>

    <!-- Tolerance Selection Filter -->
    <div class="forecast-filter forecast-filter--tolerance">
      <label class="forecast-filter__label">Select Tolerance to Highlight Drop (°C)</label>
      <div class="tolerance-btn-group" [class.dark-mode]="isDarkMode()">
        <button type="button" class="tol-btn" [class.active]="selectedTolerancePreset === 0"
          (click)="setTolerancePreset(0)">0</button>
        <button type="button" class="tol-btn" [class.active]="selectedTolerancePreset === 0.5"
          (click)="setTolerancePreset(0.5)">0.5</button>
        <button type="button" class="tol-btn" [class.active]="selectedTolerancePreset === 1"
          (click)="setTolerancePreset(1)">1</button>
        <button type="button" class="tol-btn" [class.active]="selectedTolerancePreset === 2"
          (click)="setTolerancePreset(2)">2</button>
        <button type="button" class="tol-btn" [class.active]="selectedTolerancePreset === 'custom'"
          (click)="setTolerancePreset('custom')">Custom</button>

        <div class="custom-tol-input-wrapper" *ngIf="selectedTolerancePreset === 'custom'">
          <input type="number" step="0.1" min="0" class="custom-tol-input" [value]="customToleranceValue"
            (input)="onCustomToleranceInput($any($event.target).value)" placeholder="Val" />
        </div>
      </div>
    </div>

    <div class="forecast-filter forecast-filter--expand">
      <label class="forecast-filter__label">Weather Data</label>
      <button type="button" class="forecast-expand-btn" [class.dark-mode]="isDarkMode()"
        (click)="toggleWeatherColumns()" [attr.aria-pressed]="showWeatherColumns"
        [matTooltip]="showWeatherColumns ? 'Hide Air Temp, Water Temp and Wind Speed' : 'Show Air Temp, Water Temp and Wind Speed'">
        <mat-icon>{{ showWeatherColumns ? 'cloud_off' : 'cloud' }}</mat-icon>
        <span>{{ showWeatherColumns ? 'Hide' : 'Show' }}</span>
      </button>
    </div>

    <div class="forecast-filter forecast-filter--expand" *ngIf="isSuperAdmin"> <!-- *ngIf="isSuperAdmin" -->
      <label class="forecast-filter__label">Table view</label>
      <button type="button" class="forecast-expand-btn" [class.dark-mode]="isDarkMode()" (click)="toggleTableView()"
        [matTooltip]="isCollapsedView ? 'Expand tank factors' : 'Collapse to temperatures'">
        <mat-icon>{{ isCollapsedView ? 'fullscreen' : 'close_fullscreen' }}</mat-icon>
        <span>{{ isCollapsedView ? 'Expand' : 'Collapse' }}</span>
      </button>
    </div>
  </div>

  <!-- Calibration Loading Overlay/Animation -->
  <div *ngIf="isCalibrating" class="calibration-skeleton-container">
    <app-ch-skeleton variant="table" [rows]="8" [cols]="8"></app-ch-skeleton>
  </div>

  <!-- Column Chooser Panel -->
  <div class="filter" *ngIf="showColumnChooser && !isCalibrating">
    <div class="checkbox-grid-container">
      <mat-checkbox *ngFor="let col of allColumns" [(ngModel)]="col.visible" color="primary">
        {{ col.label }}
      </mat-checkbox>
    </div>
  </div>

  <!-- Table -->
  <div class="table-container scroll-bar" *ngIf="!isCalibrating" #forecastTableContainer>
    <table class="forecast-heatplan-table text-center" id="style-4" [ngClass]="{ 'dark-table': isDarkMode() }"
      [class.has-frozen-cols]="frozenColumnCount > 0" [style.--forecast-frozen-count]="frozenColumnCount">
      <thead>
        <tr>
          <th class="sticky-col sticky-sno">S.No</th>
          <th class="sticky-col sticky-date">Date</th>
          <!-- Tank particulars live in the bar above; the tooltip is only for calculated columns -->
          <th *ngFor="let col of getVisibleColumns(); trackBy: trackByCol" [attr.data-col]="col"
            [class.sticky-col]="isFrozenColumn(col)" [class.sticky-frozen]="isFrozenColumn(col)"
            [class.sticky-frozen-last]="isLastFrozenColumn(col)" [style.left]="getFrozenColumnLeft(col)"
            [class.is-tank-col]="!!getColumnTankKey(col)" [class.is-tank-active]="isActiveTankColumn(col)"
            [class.is-tank-pinned]="isPinnedTankColumn(col)" (click)="pinTankColumn(col)"
            [attr.title]="getColumnTankKey(col) ? (isPinnedTankColumn(col) ? 'Pinned tank' : 'Click to pin this tank in the bar above') : null"
            matTooltipClass="tooltip-wide" [matTooltip]="isOutputCalcColumn(col) ? getTooltipContent(col) : ''"
            [matTooltipDisabled]="!isOutputCalcColumn(col)" matTooltipPosition="below" matTooltipShowDelay="200"
            matTooltipHideDelay="0">
            <div class="header-text">
              <mat-icon class="pin-mark" *ngIf="isPinnedTankColumn(col)" aria-hidden="true">push_pin</mat-icon>
              <ng-container *ngIf="isOutputCalcColumn(col); else heatingHeader">
                {{ getColumnDisplayName(col) }}
              </ng-container>
              <ng-template #heatingHeader>
                <ng-container *ngIf="col.includes('Heating'); else normalHeader">
                  {{ col.split(' ')[0] }}<br />{{ col.split(' ')[1] }}
                </ng-container>
                <ng-template #normalHeader>{{ col }}</ng-template>
              </ng-template>
            </div>
          </th>
          <th class="sticky-col-right w-avg-header-cell">
            <div class="header-text">W. AVG (°C) </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr *ngFor="let row of rowData; let i = index; trackBy: trackByRow" [class.first-row-locked]="i === 0"
          [class.is-editing-row]="isEditingRow(i)">
          <td class="sticky-col sticky-sno">{{ i + 1 }}</td>
          <td class="sticky-col sticky-date">
            <span class="date-cell-content">
              <span>{{ formatDate(indexes[i]) }}</span>
              <mat-icon *ngIf="i === 0" class="lock-icon"
                matTooltip="Day 1 Changes not allowed as it is actual logs received from vessel">lock</mat-icon>
            </span>
          </td>

          <td *ngFor="let col of getVisibleColumns(); trackBy: trackByCol" [attr.data-col]="col"
            [class.sticky-col]="isFrozenColumn(col)" [class.sticky-frozen]="isFrozenColumn(col)"
            [class.sticky-frozen-last]="isLastFrozenColumn(col)" [style.left]="getFrozenColumnLeft(col)"
            [class.is-tank-active]="isActiveTankColumn(col)" [class.is-editing]="isActiveEdit(i, col)"
            [class.is-edited]="i !== 0 && isEditedCell(i, col)" [class.cell-flash]="isFlashingCell(i, col)">

            <!-- ══ Output calculation totals (sum across cargo-filtered tanks) ══ -->
            <ng-container *ngIf="isOutputCalcColumn(col); else tempOrEditable">
              <span class="output-calc-value">{{ getOutputCalcCellValue(i, col) }}</span>
            </ng-container>

            <!-- ══ TEMPERATURE columns: +/- steppers + click-to-type ══
                 Steppers, editor and Δ chip are absolutely positioned, so the column width never changes. -->
            <ng-template #tempOrEditable>
              <ng-container *ngIf="isTempColumn(col); else checkEditable">
                <div class="temp-cell-wrapper">

                  <!-- Decrement (mousedown keeps the editor focused) -->
                  <button *ngIf="i !== 0" type="button" class="step-btn step-dec" tabindex="-1" title="Decrease (Alt+↓)"
                    (mousedown)="$event.preventDefault()" (click)="onStepClick(i, col, -1)">−</button>

                  <!-- Inline input (manual typing) -->
                  <ng-container *ngIf="isActiveEdit(i, col); else showTempValue">
                    <input #cellInput class="cell-input cell-input-active" type="text" inputmode="decimal"
                      autocomplete="off" spellcheck="false" [value]="row[col]" [attr.data-cell]="i + '_' + col"
                      (input)="onEditorInput(cellInput)" (blur)="finishEdit(i, col, cellInput.value)"
                      (keydown)="onEditorKeydown($event, i, col, cellInput)" />
                    <span class="edit-delta" *ngIf="getEditDelta(i, col) as delta"
                      [class.is-over]="isEditDeltaOverTolerance(i, col)" title="Change vs previous day">
                      Δ {{ delta }}
                    </span>
                  </ng-container>

                  <!-- Display value (click / Enter / type to edit) -->
                  <ng-template #showTempValue>
                    <span class="temp-value" [class.clickable]="i !== 0"
                      [class.manually-adjusted]="isManuallyAdjustedTemp(i, col)"
                      [class.temp-increased]="isTempIncreasedOverPreviousDay(i, col)"
                      [attr.tabindex]="i !== 0 ? 0 : null" [attr.data-cell]="i + '_' + col" (click)="startEdit(i, col)"
                      (keydown)="onCellKeydown($event, i, col)"
                      [attr.title]="i === 0 ? 'First day is non-editable' : 'Click or type to edit · arrows to move'">
                      {{ row[col] }}
                    </span>
                  </ng-template>

                  <!-- Increment -->
                  <button *ngIf="i !== 0" type="button" class="step-btn step-inc" tabindex="-1" title="Increase (Alt+↑)"
                    (mousedown)="$event.preventDefault()" (click)="onStepClick(i, col, 1)">+</button>
                </div>
              </ng-container>

              <!-- ══ BOILER / HEATING HRS columns: click-to-type only ══ -->
              <ng-template #checkEditable>
                <ng-container *ngIf="isEditableColumn(col); else plainCell">
                  <div class="editable-cell-wrapper">

                    <ng-container *ngIf="isActiveEdit(i, col); else showEditValue">
                      <input #editInput class="cell-input cell-input-active" type="text" autocomplete="off"
                        spellcheck="false" [value]="row[col]" [attr.data-cell]="i + '_' + col"
                        (input)="onEditorInput(editInput)" (blur)="finishEdit(i, col, editInput.value)"
                        (keydown)="onEditorKeydown($event, i, col, editInput)" />
                    </ng-container>

                    <ng-template #showEditValue>
                      <span class="editable-value" [class.clickable]="i !== 0" [attr.tabindex]="i !== 0 ? 0 : null"
                        [attr.data-cell]="i + '_' + col" (click)="startEdit(i, col)"
                        (keydown)="onCellKeydown($event, i, col)"
                        [attr.title]="i === 0 ? 'First day is non-editable' : 'Click or type to edit · arrows to move'">
                        {{ row[col] }}
                        <mat-icon *ngIf="i !== 0" class="edit-hint-icon">edit</mat-icon>
                      </span>
                    </ng-template>

                  </div>
                </ng-container>

                <!-- ══ All other columns: plain read-only ══ -->
                <ng-template #plainCell>
                  {{ row[col] }}
                </ng-template>
              </ng-template>
            </ng-template>

          </td>

          <!-- ══ Weighted Average Temperature Column (Fixed Right) ══ -->
          <td class="sticky-col-right w-avg-cell">
            <span class="w-avg-badge" [ngClass]="weightedAvgTempClass(i)"
              [class.empty-val]="formatWeightedAvg(i) === '—'">
              {{ formatWeightedAvg(i) }}<span *ngIf="formatWeightedAvg(i) !== '—'"> °C</span>
            </span>
          </td>
        </tr>
      </tbody>

      <tfoot *ngIf="rowData?.length">
        <tr class="output-calc-totals-row">
          <td class="sticky-col sticky-sno"></td>
          <td class="sticky-col sticky-date output-calc-totals-label">Total</td>
          <td *ngFor="let col of getVisibleColumns()" [class.sticky-col]="isFrozenColumn(col)"
            [class.sticky-frozen]="isFrozenColumn(col)" [class.sticky-frozen-last]="isLastFrozenColumn(col)"
            [style.left]="getFrozenColumnLeft(col)">
            <ng-container *ngIf="shouldShowColumnTotal(col); else emptyTotalCell">
              <span class="output-calc-value output-calc-value--total">
                {{ getColumnFooterTotal(col) }}
              </span>
            </ng-container>
            <ng-template #emptyTotalCell></ng-template>
          </td>
          <td class="sticky-col-right w-avg-total-cell"></td>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- Action bar: live edit status + shortcuts (left), plan actions (right) -->
  <div class="page-footer plan-action-bar">
    <div class="plan-action-bar__status">
      <span class="status-chip" [ngClass]="'status-chip--' + editStatus.tone">
        <span class="status-chip__dot"></span>
        <span class="status-chip__text">{{ editStatus.text }}</span>
      </span>

      <!-- Shortcuts: popover on hover / keyboard focus -->
      <div class="shortcuts">
        <button type="button" class="shortcuts__trigger" aria-describedby="forecast-shortcuts">
          <mat-icon>keyboard</mat-icon>
          <span>Shortcuts</span>
        </button>
        <div class="shortcuts__panel" id="forecast-shortcuts" role="tooltip">
          <div class="shortcuts__title">Editing the plan</div>
          <dl class="shortcuts__list">
            <dt><kbd>Enter</kbd></dt>
            <dd>Save &amp; next day</dd>
            <dt><kbd>Shift</kbd><kbd>Enter</kbd></dt>
            <dd>Save &amp; previous day</dd>
            <dt><kbd>Tab</kbd></dt>
            <dd>Save &amp; next tank</dd>
            <dt><kbd>Shift</kbd><kbd>Tab</kbd></dt>
            <dd>Save &amp; previous tank</dd>
            <dt><kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd></dt>
            <dd>Move between cells</dd>
            <dt><kbd>Alt</kbd><kbd>↑</kbd><kbd>↓</kbd></dt>
            <dd>Raise / lower by 1 °C</dd>
            <dt><kbd>0–9</kbd></dt>
            <dd>Start typing a new value</dd>
            <dt><kbd>Esc</kbd></dt>
            <dd>Cancel edit</dd>
          </dl>
        </div>
      </div>
    </div>

    <div class="table-controls plan-action-bar__actions">
      <button type="button" class="btn-action-modern btn-export" (click)="downloadCSV()" title="Download table as CSV">
        <mat-icon class="action-icon">download</mat-icon>
        <span>Download CSV</span>
      </button>

      <!-- Discard last change (one step at a time — works before & after calibration) -->
      <button type="button" class="btn-action-modern btn-discard" *ngIf="canDiscardChange()"
        (click)="discardLastChange()" title="Discard the last change only">
        <mat-icon class="action-icon">undo</mat-icon>
        <span>Discard</span>
      </button>

      <span class="plan-action-bar__divider" aria-hidden="true"></span>

      <!-- Calibrate (disabled until edits are made) -->
      <button type="button" class="btn-action-modern btn-calibrate" [disabled]="!hasEdits"
        [class.btn-disabled]="!hasEdits" (click)="calibrate()"
        [title]="hasEdits ? 'Calibrate temperature drop' : 'Edit a value to enable calibration'">
        <mat-icon class="action-icon">tune</mat-icon>
        <span>Calibrate</span>
      </button>

      <!-- Submit -->
      <button type="button" class="btn-action-modern btn-submit" *ngIf="!isSubmitting" (click)="submitHeatPlan()"
        title="Submit and generate final report">
        <mat-icon class="action-icon">done</mat-icon>
        <span>Submit</span>
      </button>

      <!-- Submitting Indicator -->
      <button type="button" class="btn-action-modern btn-submit is-submitting" *ngIf="isSubmitting" disabled>
        <mat-icon class="action-icon spin-icon">sync</mat-icon>
        <span>Submitting...</span>
      </button>
    </div>
  </div>

</div>


<div *ngIf="isSubmitted" class="d-flex justify-content-center align-items-center" style="height: 100vh;">
  <div class="message-box text-center">
    <h3>Submitted! Report will be generated soon.</h3>
    <p>
      Please check your reports here:
      <span class="report-link text-primary" style="cursor:pointer;" (click)="goToReports()">My Requests</span>
    </p>
  </div>
</div>